import { isSameDay } from 'date-fns';

import { parseApiDateString } from '@/utils';
import { arraysEqual } from '@/utils/arrays';

export function checkRationalesLocked(store, flashcardCategory, apptimize) {
  // Ratioanles should never be locked when onTour
  if (store.getters['session/onTour']) {
    return false;
  }

  // If Apptimize is initialized, check for the 'rationalesLocked' flag
  // If there the flag has a value, stop here. Otherwise, move on
  // If the user has paid, override the Apptimize flag and unlock all rationales
  // Otherwise, return the value from the Apptimize bool
  if (apptimize.enabled()) {
    const apptimizeBool = apptimize.root.getBool('rationalesLocked', null);

    if (apptimizeBool !== null) {
      if (store.getters['session/hasPaid']) {
        return false;
      }
      return apptimizeBool;
    }
  }

  // Finally, check the category to determine if it's a lockable category
  // If the flashcard category type is multiple choice, quiz, or userQuiz, check the POs for Paid Rationales
  if (flashcardCategory) {
    const userQuizCatType = store.getters['categoryTypes/userQuizCategoryType'];
    const multipleChoiceCatType = store.getters['categoryTypes/multipleChoiceCategoryTypes'];
    const quizCatTypes = store.getters['categoryTypes/quizCategoryTypes'];

    const gatedCategoryIds = [
      ...quizCatTypes,
      ...multipleChoiceCatType,
      userQuizCatType,
    ].filter(ct => ct).map(ct => ct.id);

    if (gatedCategoryIds.includes(flashcardCategory.categoryTypeId)) {
      const userPOIds = store.getters['session/currentUser'].purchaseOrders;
      const userPOList = store.getters['purchaseOrders/all'].filter(po => userPOIds.includes(po.id));
      const hasPaidRationales = userPOList.some(po => po.features && po.features.includes('Paid Rationales'));

      return !hasPaidRationales;
    }
  }

  // If none of these conditions are true, rationales are not locked
  return false;
}

export function hasAdvancedStats(store, apptimize, analytics) {
  if (store.getters['session/onTour']) {
    return false;
  }

  // disabling this because PO flags should take precedent now and the experiments aren't needed
  // if (apptimize?.enabled()) {
  //   const advancedStats = apptimize.checkExperiments({
  //     experiment: 'Advanced Flashcard Stats',
  //     bool: 'advancedFlashcardStats',
  //     defaultVal: null,
  //     matcher: /advanced flashcard stats/gi,
  //     analytics: analytics,
  //   });

  //   return advancedStats;
  // }

  const userPOIds = store.getters['session/currentUser'] ? store.getters['session/currentUser'].purchaseOrders : [];
  const userPOList = store.getters['purchaseOrders/all'].filter(po => userPOIds.includes(po.id));
  return userPOList.some(po => po.features && po.features.includes('Advanced Flashcard Stats'));
}

/**
 * Normalizes flashcard.numSelections to a positive integer, or returns null
 * if it is missing, falsy, or not a positive integer. Accepts numeric strings.
 * @param {Object} flashcard
 * @returns {number|null}
 */
export function getNumSelections(flashcard) {
  const raw = flashcard?.numSelections;
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Returns true if a SATA flashcard's numSelections constraint is satisfied
 * (or absent). Used by v2 surfaces to gate submit; v1 callers can ignore this.
 * @param {Object} flashcard
 * @param {Array} answerIds
 * @returns {boolean}
 */
export function numSelectionsSatisfied(flashcard, answerIds) {
  if (flashcard?.type !== 'SATA') {
    return true;
  }
  const required = getNumSelections(flashcard);
  if (required === null) {
    return true;
  }
  return answerIds.length === required;
}

export function hasAnsweredFlashcard(flashcard, answerIds, rawAnswerData, terminology = false) {
  if (!flashcard) {
    return false;
  }
  if (flashcard.type === 'Hotspot') {
    return isHotspotAnswered(rawAnswerData);
  } else if (flashcard.type === 'Blank Text Completion') {
    return answerIds.length === flashcard.answers.length / flashcard.blankTextColumns;
  } else if (flashcard.type === 'Ordered Response') {
    return isOrderedResponseAnswered(flashcard, rawAnswerData);
  } else if (flashcard.type === 'Numeric Entry') {
    return isNumericEntryAnswered(rawAnswerData);
  } else if (flashcard.type === 'Fractional Numeric Entry') {
    return isFractionalNumericEntryAnswered(rawAnswerData);
  } else if (flashcard.type === 'Matrix Grid') {
    return isMatrixGridAnswered(rawAnswerData, flashcard.matrixGridSelectionType);
  } else if (flashcard.type === 'Bowtie') {
    return answerIds.length === 5;
  } else {
    return answerIds.length > 0 || terminology === true;
  }
}

function isHotspotAnswered(rawAnswerData) {
  if (!rawAnswerData) {
    return false;
  } else if (Number.isFinite(rawAnswerData.x) && Number.isFinite(rawAnswerData.y)) {
    return true;
  } else {
    return false;
  }
}

function isOrderedResponseAnswered(flashcard, rawAnswerData) {
  if (!rawAnswerData) {
    return false;
  } else if (rawAnswerData.orderedAnswerIds) {
    const originalAnswerIds = flashcard.answers.map(answer => answer.id);
    return !arraysEqual(originalAnswerIds, rawAnswerData.orderedAnswerIds);
  } else {
    return false;
  }
}

function isNumericEntryAnswered(rawAnswerData) {
  if (!rawAnswerData) {
    return false;
  } else if (rawAnswerData.numeric != null && rawAnswerData.numeric !== '') {
    return true;
  } else {
    return false;
  }
}

function isFractionalNumericEntryAnswered(rawAnswerData) {
  if (!rawAnswerData) {
    return false;
  } else if (
    (rawAnswerData.numerator != null && rawAnswerData.numerator !== '') &&
    (rawAnswerData.denominator != null && rawAnswerData.denominator !== '')
  ) {
    return true;
  } else {
    return false;
  }
}

function isMatrixGridAnswered(rawAnswerData, selectionType) {
  if (!rawAnswerData) {
    return false;
  }
  const selections = rawAnswerData.matrixGridSelections;
  if (!selections || selections.length === 0) {
    return false;
  }
  if (selectionType === 'one_per_column') {
    const columnCount = selections[0].selections.length;
    return Array.from({ length: columnCount }).every(
      (_, column) => selections.some(row => row.selections[column] === true),
    );
  }
  return selections.every(row => row.selections.some(value => value === true));
}

export function flashcardType(flashcard) {
  if (flashcard.type === 'Flipcard') {
    return 'terminology';
  } else if (flashcard.type === 'SATA') {
    return 'multiple-choice';
  } else if (flashcard.type === 'Cloze / Dropdown') {
    return 'cloze-dropdown';
  } else if (flashcard.type) {
    return flashcard.type.toLowerCase().split(' ').join('-');
  }
}

export function flashcardStatsByDay(stats, day = new Date()) {
  const dayStats = stats.flashcard?.find(stat => {
    const statDate = parseApiDateString(stat.date).date;
    return isSameDay(statDate, day);
  });
  return dayStats?.count || 0;
}

export function checkMatrixGridAnswerCorrectness(flashcard, matrixGridSelections, answerId) {
  const userAnswer = matrixGridSelections?.find(answerObj => answerObj.answerId === answerId)?.selections || [];
  const flashcardAnswer = flashcard.answers.find(answer => answer.id === answerId)?.matrixGridCorrect || [];
  return userAnswer.every((answer, index) => answer === flashcardAnswer[index]);
}

export function checkMatrixGridAnswerAnswered(matrixGridSelections, answerId) {
  // This determines if a given answer has any true values indicating that a user has answered it
  return matrixGridSelections?.find(answerObj => answerObj.answerId === answerId)?.selections?.some(answer => answer);
}

export function checkMatrixGridSelectionCorrectness(flashcard, matrixGridSelections, answerId, selectionIndex) {
  const userAnswer = getMatrixGridUserAnswer(matrixGridSelections, answerId, selectionIndex);
  const flashcardAnswer = getMatrixGridFlashcardAnswer(flashcard, answerId, selectionIndex);
  if (flashcardAnswer === true) {
    return 'correct';
  } else if (userAnswer === true && flashcardAnswer === false) {
    return 'incorrect';
  }
}

export function checkMatrixGridSelectionSelected(matrixGridSelections, answerId, selectionIndex) {
  const userAnswer = getMatrixGridUserAnswer(matrixGridSelections, answerId, selectionIndex);
  return userAnswer === true;
}

export function getMatrixGridUserAnswer(matrixGridSelections, answerId, selectionIndex) {
  return matrixGridSelections?.find(answerObj => answerObj.answerId === answerId)?.selections[selectionIndex];
}

export function getMatrixGridFlashcardAnswer(flashcard, answerId, selectionIndex) {
  return flashcard?.answers?.find(answer => answer.id === answerId)?.matrixGridCorrect[selectionIndex];
}
